import { useEffect, useCallback } from 'react'
import { useSearchParams, useParams } from 'react-router-dom'
import { createSelector, isFulfilled } from '@reduxjs/toolkit'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  getDashboardInfos,
  getRecentViewedDashboardInfos
} from 'features/dashboardInfo/asyncThunks'
import { getPageConfig } from 'features/config/asyncThunks'
import { retrieveEmojis } from 'features/emoji/asyncThunks'
import { DashboardCategory } from 'features/dashboardInfo/types'
import { setPresentationMode } from 'features/ui/uiSlice'
import { CATEGORY } from 'features/dashboardInfo/constants'
import { clearMemorizedStateFromSessionStorage } from 'utils/sessionStorageUtils'
import { sendTypedMessage } from 'utils/iframeUtil'
import i18n from 'i18n'

export const useRetrieveDashboardInfos = (category?: DashboardCategory) => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (category === CATEGORY.recentlyViewedDashboards) {
      dispatch(getRecentViewedDashboardInfos({ staleTime: 0 }))
    } else {
      dispatch(getDashboardInfos({ category, options: { staleTime: 0 } }))
    }
  }, [dispatch, category])
}

export const useRetrievePageConfig = () => {
  const dispatch = useAppDispatch()
  const pageConfigState = useAppSelector(({ pageConfig }) => pageConfig)

  useEffect(() => {
    const fetchPageConfig = async () => {
      // * might want to add conditions to prevent fetching again
      const res = await dispatch(getPageConfig())

      // * set language after fetching page config
      if (isFulfilled(res) && res.payload.pageConfig.user.locale === 'ja_JP') {
        i18n.changeLanguage('ja')
      }
    }
    fetchPageConfig()
  }, [dispatch])
  return pageConfigState
}

export const useRetrieveEmojis = () => {
  const dispatch = useAppDispatch()
  const emojiState = useAppSelector(({ emoji }) => emoji)
  useEffect(() => {
    // * might want to add conditions to prevent fetching again
    dispatch(retrieveEmojis())
  }, [dispatch])
  return emojiState
}

export const useRetrievePreRequiredRequests = () => {
  const { status: pageConfigStatus } = useRetrievePageConfig()
  const { status: emojiStatus } = useRetrieveEmojis()
  return { pageConfigStatus, emojiStatus }
}

export const usePresentationModeUrlHandler = () => {
  const { pageConfigStatus, dashboardStatus } = useAppSelector(
    createSelector(
      [
        (state) => state.ui.dashboardViewLayout.presentationMode,
        (state) => state.pageConfig.status,
        (state) => state.dashboard.status
      ],
      (pMode, pStatus, dStatus) => ({
        presentationMode: pMode,
        pageConfigStatus: pStatus,
        dashboardStatus: dStatus
      })
    )
  )

  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()

  // * first load and searchParams changes dispatch presentation mode or not
  useEffect(() => {
    const finalStatus = ['succeeded', 'failed']
    if (finalStatus.includes(pageConfigStatus) && finalStatus.includes(dashboardStatus)) {
      const urlHasPresentation = searchParams.get('presentation-mode')
      dispatch(setPresentationMode(urlHasPresentation === 'true'))
    }
  }, [pageConfigStatus, searchParams, dispatch, dashboardStatus])
}

export const useSetPresentationModeUrl = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const setFunc = useCallback(
    (isPresentationMode: boolean) => {
      if (isPresentationMode) {
        searchParams.append('presentation-mode', 'true')
      } else {
        searchParams.delete('presentation-mode')
      }
      setSearchParams(searchParams)
    },
    [searchParams, setSearchParams]
  )

  return setFunc
}

export const useExitNewDashboardMode = () => {
  const { dashboardId } = useParams()

  const exitFunc = useCallback(() => {
    clearMemorizedStateFromSessionStorage()
    if (dashboardId) {
      sendTypedMessage('ExitFullScreen', dashboardId)
    } else {
      sendTypedMessage('ExitFullScreen', -1)
    }
  }, [dashboardId])

  return exitFunc
}
